import {
    Box,
    Card,
    Typography,
    Divider,
    Button,
    Grid,
    CircularProgress,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Link, // Import Link for clickable URLs
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import COLORS from "../../../../../constants/colors";
import baseURL from "../../../user/config";
import { toast } from "react-toastify";

// Define a type or enum for organization types
const ORG_TYPES = {
    CSO: 'CSO',
    NGO: 'NGO',
};

// Reusable component for file upload box
const UploadBox = ({ label, docType, file, preview, onFileChange, onRemoveFile }) => {
    const showRemoveButton = file !== null;

    return (
        <Box
            sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                p: 2,
                textAlign: "center",
                cursor: file ? "default" : "pointer",
                position: "relative",
                minHeight: "150px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                "&:hover": {
                    borderColor: file ? "#ccc" : COLORS.primary,
                    bgcolor: file ? "transparent" : "#f5f7fa",
                },
            }}
            component="label"
        >
            {file ? (
                <Box sx={{ position: "relative", width: "100%", height: "100%", display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "3rem", color: "#d32f2f" }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {label} Uploaded
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                        {file.name || 'document.pdf'}
                    </Typography>
                    {showRemoveButton && (
                        <IconButton
                            onClick={(e) => {
                                e.stopPropagation();
                                onRemoveFile(docType);
                            }}
                            sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                bgcolor: "rgba(255, 255, 255, 0.7)",
                                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.9)" },
                            }}
                        >
                            <CancelOutlinedIcon color="error" />
                        </IconButton>
                    )}
                </Box>
            ) : (
                <>
                    <CloudUploadOutlinedIcon sx={{ fontSize: "3rem", color: "#9e9e9e" }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        (PDF, Max 2MB)
                    </Typography>
                </>
            )}
            <input
                type="file"
                hidden
                accept=".pdf"
                onChange={(e) => onFileChange(e, docType)}
            />
        </Box>
    );
};

// Reusable component to display an uploaded document
const UploadedDocDisplay = ({ label, fileUrl }) => {
    if (!fileUrl) return null;

    const fileName = fileUrl.split('/').pop();
    const displayName = label.replace("Certificate", "").replace("Permit", "").trim();

    return (
        <Box
            sx={{
                border: `2px solid ${COLORS.border}`,
                borderRadius: 2,
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '150px',
                textAlign: 'center',
                bgcolor: '#f9f9f9',
            }}
        >
            <AddPhotoAlternateOutlinedIcon sx={{ fontSize: "3rem", color: COLORS.primary }} />
            <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                {displayName} Uploaded
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                {fileName}
            </Typography>
            <Link href={fileUrl} target="_blank" rel="noopener" sx={{ mt: 1 }}>
                View Document
            </Link>
        </Box>
    );
};

const KycDocsUpload = () => {
    const [orgType, setOrgType] = useState(null);
    const [files, setFiles] = useState({
        csoCertificate: null,
        ngoCertificate: null,
        ngoPermit: null,
    });
    const [loading, setLoading] = useState(false);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);
    const [fetchingStatus, setFetchingStatus] = useState(true);
    const [hasUploaded, setHasUploaded] = useState(false);
    const [uploadedDocs, setUploadedDocs] = useState({});

    // Fetch the organization profile and its KYC status
    const fetchOrgData = useCallback(async () => {
        try {
            const profileResponse = await fetch(`${baseURL}/org/profile`, { credentials: 'include' });
            if (!profileResponse.ok) throw new Error("Failed to fetch organization profile.");

            const profileData = await profileResponse.json();
            setOrgType(profileData.organization.orgType);

            // Fetch the KYC status from the new endpoint
            const kycResponse = await fetch(`${baseURL}/org/check-kyc`, { credentials: 'include' });
            if (kycResponse.ok) {
                const kycData = await kycResponse.json();
                setHasUploaded(true);
                setUploadedDocs(kycData.data);
            }
        } catch (error) {
            console.error("Error fetching organization data:", error);
            // If the error is a 404, it just means no KYC docs are uploaded yet.
            if (error.message !== 'Failed to fetch organization profile.' && error.message !== 'No KYC documents found for this organization.') {
                toast.error(`Error: ${error.message}`);
            }
        } finally {
            setFetchingStatus(false);
        }
    }, []);

    useEffect(() => {
        fetchOrgData();
    }, [fetchOrgData]);

    const handleFileChange = (event, docType) => {
        const file = event.target.files[0];
        if (file) {
            const allowedTypes = ['application/pdf'];
            const maxFileSize = 2 * 1024 * 1024; // 2 MB

            if (!allowedTypes.includes(file.type)) {
                toast.error("Please upload a PDF file.");
                return;
            }

            if (file.size > maxFileSize) {
                toast.error("File size exceeds 2 MB limit.");
                return;
            }

            setFiles(prevFiles => ({ ...prevFiles, [docType]: file }));
        }
    };

    const handleRemoveFile = (docType) => {
        setFiles(prevFiles => ({ ...prevFiles, [docType]: null }));
    };

    const handleUploadSubmit = async () => {
        const formData = new FormData();

        if (orgType === ORG_TYPES.CSO) {
            if (!files.csoCertificate) {
                toast.error("Please upload the CSO Certificate of Registration.");
                return;
            }
            formData.append("csoCertificate", files.csoCertificate);
        } else if (orgType === ORG_TYPES.NGO) {
            if (!files.ngoCertificate || !files.ngoPermit) {
                toast.error("Please upload both the NGO Certificate of Incorporation and NGO Permit.");
                return;
            }
            formData.append("ngoCertificate", files.ngoCertificate);
            formData.append("ngoPermit", files.ngoPermit);
        } else {
            toast.error("Invalid organization type. Cannot upload documents.");
            return;
        }

        setLoading(true);

        try {
            const response = await fetch(`${baseURL}/org/upload-kyc`, {
                method: 'POST',
                body: formData,
                credentials: 'include',
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Failed to upload KYC documents.');
            }

            console.log("KYC documents uploaded successfully:", data.data);
            setFiles({ csoCertificate: null, ngoCertificate: null, ngoPermit: null });
            setLoading(false);
            setIsSuccessDialogOpen(true);
            setHasUploaded(true);
            setUploadedDocs(data.data);
        } catch (error) {
            console.error("Error uploading KYC documents:", error);
            setLoading(false);
            toast.error(`Error: ${error.message}`);
        }
    };

    const isUploadDisabled = () => {
        if (hasUploaded) {
            return true;
        }
        if (orgType === ORG_TYPES.CSO) {
            return !files.csoCertificate || loading;
        }
        if (orgType === ORG_TYPES.NGO) {
            return !files.ngoCertificate || !files.ngoPermit || loading;
        }
        return true;
    };

    if (fetchingStatus) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!orgType) {
        return (
            <Box sx={{ mt: 3, mx: "auto", textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Your organization type could not be determined.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ mt: 3, mx: "auto" }}>
            <Card
                sx={{
                    backgroundColor: "#fff",
                    borderRadius: "16px",
                    boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
                    border: "1px solid rgba(0,0,0,0.05)",
                    p: 3,
                    mb: 3,
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AddPhotoAlternateOutlinedIcon
                        sx={{ fontSize: "1.6rem", mr: 1.5, color: "#2D4A3D" }}
                    />
                    <Typography variant="h6" sx={{ fontWeight: 800, color: "#1a1a2e", fontSize: "1.05rem", letterSpacing: "-0.3px" }}>
                        Organization Documents Upload
                    </Typography>
                </Box>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    To verify your organization, please upload the required documents in PDF format. The maximum file size is 2MB.
                </Typography>

                {hasUploaded ? (
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Box
  sx={{
    border: 1,
    borderColor: 'info.main',
    borderRadius: 2,
    p: 2,
    mb: 2,
    backgroundColor: 'info.lighter', // optional for slight background
  }}
>
  <Typography
    variant="body1"
    align="left"
    sx={{ color: 'info.main' }}
  >
    Your documents have been submitted. You can view them below. Please wait while we verify your submission.
  </Typography>
</Box>
                        </Grid>
                        {orgType === ORG_TYPES.CSO && (
                            <Grid item xs={12}>
                                <UploadedDocDisplay label="CSO Certificate of Registration" fileUrl={uploadedDocs.csoCertificateUrl} />
                            </Grid>
                        )}
                        {orgType === ORG_TYPES.NGO && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <UploadedDocDisplay label="NGO Certificate of Incorporation" fileUrl={uploadedDocs.ngoCertificateUrl} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <UploadedDocDisplay label="NGO Permit" fileUrl={uploadedDocs.ngoPermitUrl} />
                                </Grid>
                            </>
                        )}
                    </Grid>
                ) : (
                    <Grid container spacing={3}>
                        {orgType === ORG_TYPES.CSO && (
                            <Grid item xs={12}>
                                <UploadBox
                                    label="CSO Certificate of Registration"
                                    docType="csoCertificate"
                                    file={files.csoCertificate}
                                    onFileChange={handleFileChange}
                                    onRemoveFile={handleRemoveFile}
                                />
                            </Grid>
                        )}
                        {orgType === ORG_TYPES.NGO && (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <UploadBox
                                        label="NGO Certificate of Incorporation"
                                        docType="ngoCertificate"
                                        file={files.ngoCertificate}
                                        onFileChange={handleFileChange}
                                        onRemoveFile={handleRemoveFile}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <UploadBox
                                        label="NGO Permit"
                                        docType="ngoPermit"
                                        file={files.ngoPermit}
                                        onFileChange={handleFileChange}
                                        onRemoveFile={handleRemoveFile}
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                )}
                
                {/* Dynamically show the upload button or a status message */}
                {hasUploaded ? (
                    <Button
                        variant="contained"
                        disabled
                        fullWidth
                        sx={{ mt: 3, textTransform: "none", backgroundColor: '#2D4A3D' }}
                    >
                        Documents Submitted
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        startIcon={<CloudUploadOutlinedIcon />}
                        onClick={handleUploadSubmit}
                        disabled={isUploadDisabled()}
                        fullWidth
                        sx={{ mt: 3, textTransform: "none", backgroundColor: '#2D4A3D', '&:hover': { backgroundColor: '#2D4A3D', opacity: 0.9 }, boxShadow: 0 }}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : "Upload Documents"}
                    </Button>
                )}
            </Card>

            {/* Success Dialog */}
            <Dialog open={isSuccessDialogOpen} onClose={() => setIsSuccessDialogOpen(false)}>
                <DialogTitle sx={{ textAlign: 'center' }}>Upload Successful! 🎉</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" align="center">
                        Your KYC documents have been successfully uploaded for verification. We will review them shortly.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center' }}>
                    <Button onClick={() => setIsSuccessDialogOpen(false)} variant="contained" autoFocus sx={{ backgroundColor: '#2D4A3D', '&:hover': { backgroundColor: '#2D4A3D', opacity: 0.9 }, boxShadow: 0, textTransform: 'none' }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default KycDocsUpload;